import React, { useState } from 'react';
import { ErrorMessage, EmptyState, LoadingSpinner } from '../components/ui';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import { useSectionHashTabSwitch } from '../hooks/useSectionHashTabSwitch';
import CustomerPurchaseBehaviorGroupCountByMonthChart from '../components/customerPurchaseBehavior/CustomerPurchaseBehaviorGroupCountByMonthChart';
import CustomerPurchaseBehaviorCountChangeByGroupChart from '../components/customerPurchaseBehavior/CustomerPurchaseBehaviorCountChangeByGroupChart';
import CustomerPurchaseBehaviorStackedPurchaseChart from '../components/customerPurchaseBehavior/CustomerPurchaseBehaviorStackedPurchaseChart';
import CustomerPurchaseBehaviorGroupGrowthByTransition from '../components/customerPurchaseBehavior/CustomerPurchaseBehaviorGroupGrowthByTransition';
import CustomerPurchaseBehaviorGroupGrowthByTransitionReturningCustomers from '../components/customerPurchaseBehavior/CustomerPurchaseBehaviorGroupGrowthByTransitionReturningCustomers';
import { useCustomerPurchaseBehaviorData } from '../components/customerPurchaseBehavior/customerPurchaseBehaviorData';
import { useTabKeyboardNav } from '../hooks/useTabKeyboardNav';

interface CustomerPurchaseBehaviorDashboardProps {
  branchId?: number;
}

type ActiveCustomerPurchaseBehaviorTab =
  | 'groupGrowthByTransition'
  | 'growthByMonth'
  | 'groupCountByMonth'
  | 'groupGrowthByTransitionReturningCustomers';

const customerPurchaseTabs = [
  { id: 'groupGrowthByTransition', label: 'رشد هر گروه (٪)' },
  { id: 'growthByMonth', label: 'تغییر تعداد هر گروه' },
  { id: 'groupCountByMonth', label: 'روند یک گروه' },
  { id: 'groupGrowthByTransitionReturningCustomers', label: 'رشد: جدید و بازگشتی' },
] as const;

const CPB_SECTION_TABS: Record<string, ActiveCustomerPurchaseBehaviorTab> = {
  'customer-purchase-behavior-group-growth': 'groupGrowthByTransition',
  'customer-purchase-behavior-count-change-by-group': 'growthByMonth',
  'customer-purchase-behavior-group-count-by-month': 'groupCountByMonth',
  'customer-purchase-behavior-group-growth-returning-customers': 'groupGrowthByTransitionReturningCustomers',
};

const sharedChartProps = (
  branchId: number | undefined,
  data: ReturnType<typeof useCustomerPurchaseBehaviorData>['data'],
  refetch: ReturnType<typeof useCustomerPurchaseBehaviorData>['refetch'],
) => ({
  branchId,
  purchaseData: data,
  onRetry: () => {
    refetch();
  },
});

const CustomerPurchaseBehaviorDashboard: React.FC<CustomerPurchaseBehaviorDashboardProps> = ({ branchId }) => {
  const { data, isLoading, error, refetch } = useCustomerPurchaseBehaviorData(branchId);
  const [activeTab, setActiveTab] = useState<ActiveCustomerPurchaseBehaviorTab>('groupGrowthByTransition');
  useSectionHashTabSwitch(CPB_SECTION_TABS, setActiveTab);
  const tabIds = customerPurchaseTabs.map((tab) => tab.id) as ActiveCustomerPurchaseBehaviorTab[];
  const { onKeyDown, registerTab } = useTabKeyboardNav<ActiveCustomerPurchaseBehaviorTab>(
    tabIds,
    activeTab,
    setActiveTab,
  );
  const chartProps = sharedChartProps(branchId, data, refetch);

  if (!branchId) {
    return (
      <div dir="rtl" className="container-dashboard">
        <div className="card-base card-padding border border-status-warning" role="alert">
          برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner variant="dashboard" message="در حال بارگذاری رفتار خرید مشتریان…" />;
  }

  if (error) {
    return (
      <div dir="rtl" className="container-dashboard">
        <div className="card-base card-padding">
          <ErrorMessage
            message="دریافت اطلاعات رفتار خرید مشتریان با خطا مواجه شد. لطفاً دوباره تلاش کنید."
            onRetry={() => {
              refetch();
            }}
          />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div dir="rtl" className="container-dashboard">
        <div className="card-base card-padding">
          <EmptyState message="برای این شعبه هنوز اطلاعات رفتار خریدی ثبت نشده است" />
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="container-dashboard">

      <div className="text-center">
          <h1 className="title-dashboard">رفتار خرید مشتریان</h1>
          <p className="text-sm text-text-secondary mt-2">
            دسته‌بندی مشتریان در ۱۲ ماه اخیر و بررسی رشد ماه‌به‌ماه نسبت به ماه قبل
          </p>
        </div>
      <div className="space-y-8 md:space-y-10 animate-enter-up">

        <DashboardSectionAnchor sectionId="customer-purchase-behavior-stacked">
          <CustomerPurchaseBehaviorStackedPurchaseChart {...chartProps} />
        </DashboardSectionAnchor>

        <div className="card-base card-padding animate-enter animate-delay-3">
          <div className="border-b border-border-secondary">
            <div
              role="tablist"
              aria-label="نمای رشد گروه‌های خرید"
              className="flex mobile-tab-scroll lg:flex-wrap items-stretch lg:items-center justify-start lg:justify-center -mb-px w-full max-w-full"
            >
              {customerPurchaseTabs.map((tab, index) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    ref={registerTab(tab.id)}
                    id={`cpb-tab-${tab.id}`}
                    role="tab"
                    type="button"
                    aria-selected={isActive}
                    aria-controls={`cpb-panel-${tab.id}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveTab(tab.id)}
                    onKeyDown={onKeyDown}
                    className={`
                      relative flex items-center justify-center gap-2 px-4 sm:px-8 py-2.5 text-sm sm:text-base transition-all duration-300 flex-shrink-0
                      ${index > 0 ? 'mr-2' : ''}
                      ${isActive
                        ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]'
                        : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'
                      }
                    `}
                  >
                    {/* وزن فونت به‌عنوان نشانه‌ی غیر-رنگی برای تب فعال */}
                    <span className={`text-sm lg:text-lg ${isActive ? 'font-bold' : 'font-medium'}`}>{tab.label}</span>
                    {isActive && (
                      <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            role="tabpanel"
            id={`cpb-panel-${activeTab}`}
            aria-labelledby={`cpb-tab-${activeTab}`}
            tabIndex={0}
            className="animate-enter mobile-tab-panel"
          >
            {activeTab === 'groupGrowthByTransition' && (
              <DashboardSectionAnchor sectionId="customer-purchase-behavior-group-growth">
                <CustomerPurchaseBehaviorGroupGrowthByTransition {...chartProps} />
              </DashboardSectionAnchor>
            )}
            {activeTab === 'growthByMonth' && (
              <DashboardSectionAnchor sectionId="customer-purchase-behavior-count-change-by-group">
                <CustomerPurchaseBehaviorCountChangeByGroupChart {...chartProps} />
              </DashboardSectionAnchor>
            )}
            {activeTab === 'groupCountByMonth' && (
              <DashboardSectionAnchor sectionId="customer-purchase-behavior-group-count-by-month">
                <CustomerPurchaseBehaviorGroupCountByMonthChart {...chartProps} />
              </DashboardSectionAnchor>
            )}
            {activeTab === 'groupGrowthByTransitionReturningCustomers' && (
              <DashboardSectionAnchor sectionId="customer-purchase-behavior-group-growth-returning-customers">
                <CustomerPurchaseBehaviorGroupGrowthByTransitionReturningCustomers {...chartProps} />
              </DashboardSectionAnchor>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPurchaseBehaviorDashboard;
